import { ApolloError } from '@apollo/client';
import { PoNumberDisplay } from '@/enums';
import { useCallback } from 'react';
import { useSalesPortalChatPoNumberDisplayModeLoader } from '@/experiences/sales-portal/blocs/loaders/use-sales-portal-chat-po-number-display-mode-loader';
import { useSalesPortalSupplierMetadataLoader } from '@/experiences/sales-portal/blocs/loaders/use-sales-portal-supplier-metadata-loader';

interface SalesPortalPoNumberDisplayModeParams {
  restaurantUUID: string;
  supplierUUID: string;
}

interface SalesPortalPoNumberDisplayModeResult {
  error: ApolloError | undefined;
  loading: boolean;
  poNumberDisplayMode: PoNumberDisplay;
  refetch: () => Promise<unknown>;
  refetching: boolean;
}

/**
 * Resolves the effective PO number display setting for a restaurant/supplier
 * pair: the chat (account) level override first, falling back to the supplier
 * level default. Mirrors the storefront resolution in `new-order-context`.
 *
 * @param params.restaurantUUID - Restaurant UUID of the account
 * @param params.supplierUUID - Supplier UUID
 *
 * @returns the effective poNumberDisplayMode, a loading flag covering both
 *   queries, the first error of either query (callers should not treat a failed
 *   query as "no override" or as the Hide default), and a refetch that retries
 *   both queries. Apollo clears `error` while a refetch is in flight, so callers
 *   that render an error state should keep it on screen while `refetching` is
 *   true instead of flickering back to content
 *
 * @example
 * ```typescript
 * const { error, loading, poNumberDisplayMode } = useSalesPortalPoNumberDisplayMode({
 *   restaurantUUID,
 *   supplierUUID,
 * });
 * ```
 */
export const useSalesPortalPoNumberDisplayMode = ({
  restaurantUUID,
  supplierUUID,
}: SalesPortalPoNumberDisplayModeParams): SalesPortalPoNumberDisplayModeResult => {
  const {
    error: supplierError,
    loading: supplierLoading,
    poNumberDisplayMode: supplierPoNumberDisplayMode,
    refetch: refetchSupplierMetadata,
    refetching: supplierRefetching,
  } = useSalesPortalSupplierMetadataLoader({
    supplierUUID,
  });
  const {
    error: chatError,
    loading: chatLoading,
    poNumberDisplayMode: chatPoNumberDisplayMode,
    refetch: refetchChat,
    refetching: chatRefetching,
  } = useSalesPortalChatPoNumberDisplayModeLoader({
    restaurantUUID,
    supplierUUID,
  });

  const refetch = useCallback(
    () => Promise.all([refetchSupplierMetadata(), refetchChat()]),
    [refetchChat, refetchSupplierMetadata],
  );

  return {
    error: chatError ?? supplierError,
    loading: supplierLoading || chatLoading,
    poNumberDisplayMode: chatPoNumberDisplayMode || supplierPoNumberDisplayMode,
    refetch,
    refetching: supplierRefetching || chatRefetching,
  };
};
